import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, addDoc, getDocs, serverTimestamp, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebaseConfig';
import { ChevronLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Alert, Spinner } from 'react-bootstrap';

// Yeni componentleri import ediyoruz
import CampaignEditor from './components/CampaignEditor';
import RecipientManager from './components/RecipientManager';

const CreateCampaign = () => {
    // --- State Yönetimi ---
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    
    // Alıcı Listesi (Set kullanarak duplicate engelliyoruz)
    const [selectedRecipients, setSelectedRecipients] = useState(new Set());
    
    // Veri Kaynakları
    const [professionals, setProfessionals] = useState([]);
    const [contactMessages, setContactMessages] = useState([]);
    
    const navigate = useNavigate();
    const functions = getFunctions();

    // --- Verileri Çekme ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Professionals
                const profQuery = query(collection(db, 'professionals'), where('status', '==', 'approved'));
                const profSnapshot = await getDocs(profQuery);
                const profList = profSnapshot.docs
                    .map(doc => doc.data().email)
                    .filter(email => email && email.includes('@'));
                setProfessionals(profList);

                // Messages
                const msgSnapshot = await getDocs(collection(db, 'contactMessages'));
                const msgList = msgSnapshot.docs
                    .map(doc => doc.data().email)
                    .filter(email => email && email.includes('@'));
                setContactMessages([...new Set(msgList)]); 

            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, []);

    // --- Alıcı İşlemleri ---
    const handleAddRecipients = (emailList) => {
        const updated = new Set(selectedRecipients);
        emailList.forEach(email => updated.add(email));
        setSelectedRecipients(updated);
    };

    const handleClearRecipients = () => {
        if(window.confirm('Are you sure you want to clear the entire recipient list?')) {
            setSelectedRecipients(new Set());
        }
    };

    // --- Kaydetme / Gönderme Mantığı ---
    const handleAction = async (actionType) => {
        if (!subject.trim() || !body.trim() || body === '<p><br></p>') {
            setMessage({ text: 'Subject and content cannot be empty.', type: 'danger' });
            return;
        }

        if (actionType === 'send' && selectedRecipients.size === 0) {
            setMessage({ text: 'Please add at least one recipient.', type: 'warning' });
            return;
        }

        const isLoadingState = actionType === 'send' ? setSending : setLoading;
        isLoadingState(true);
        setMessage({ text: '', type: '' });

        try {
            const recipientArray = Array.from(selectedRecipients);

            // 1. Veritabanına Kaydet
            const docRef = await addDoc(collection(db, 'campaigns'), {
                subject: subject,
                body: body,
                recipients: recipientArray,
                recipientCount: recipientArray.length,
                status: actionType === 'send' ? 'queued' : 'draft',
                createdAt: serverTimestamp(),
            });

            if (actionType === 'draft') {
                setMessage({ text: 'Draft saved successfully!', type: 'success' });
                setTimeout(() => navigate('/admin/manage-campaigns'), 1500);
            } 
            else if (actionType === 'send') {
                // 2. Cloud Function Tetikle
                const sendCampaignFn = httpsCallable(functions, 'sendCampaign');
                const result = await sendCampaignFn({ campaignId: docRef.id });
                
                if (result.data.success) {
                    setMessage({ text: 'Campaign sent successfully! 🚀', type: 'success' });
                    setTimeout(() => navigate('/admin/manage-campaigns'), 2000);
                } else {
                    throw new Error(result.data.message || 'Sending failed');
                }
            }
        } catch (error) {
            console.error("Error:", error);
            setMessage({ text: 'Error: ' + error.message, type: 'danger' });
        } finally {
            isLoadingState(false);
        }
    };

    return (
        <div className="container-fluid p-4 bg-light min-vh-100">
            <form onSubmit={(e) => e.preventDefault()}>
                {/* Header Section */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <div className="mb-3 mb-md-0">
                        <h1 className="fw-bold h3 text-dark mb-1">Create Campaign</h1>
                        <p className="text-muted mb-0">Design beautiful emails for your subscribers.</p>
                    </div>
                    
                    <div className="d-flex gap-2">
                        <Link to="/admin/manage-campaigns" className="btn btn-outline-secondary px-3 d-flex align-items-center">
                            <ChevronLeftIcon className="h-5 w-5 me-1" style={{width:'20px'}} /> Back
                        </Link>
                        <button 
                            className="btn btn-white border px-3 fw-medium" 
                            onClick={() => handleAction('draft')}
                            disabled={loading || sending}
                        >
                            {loading ? 'Saving...' : 'Save Draft'}
                        </button>
                        <button 
                            className="btn btn-primary px-4 fw-bold d-flex align-items-center gap-2 shadow-sm" 
                            onClick={() => handleAction('send')}
                            disabled={loading || sending}
                        >
                            {sending ? <Spinner size="sm" animation="border" /> : <PaperAirplaneIcon className="h-5 w-5" style={{width:'20px'}} />}
                            {sending ? 'Sending...' : 'Send Campaign'}
                        </button>
                    </div>
                </div>

                {message.text && (
                    <Alert variant={message.type} className="shadow-sm border-0 mb-4">
                        {message.text}
                    </Alert>
                )}

                <div className="row g-4">
                    {/* SOL PANEL: Editör */}
                    <div className="col-lg-8">
                        <CampaignEditor 
                            subject={subject} 
                            setSubject={setSubject} 
                            body={body} 
                            setBody={setBody} 
                        />
                    </div>

                    {/* SAĞ PANEL: Alıcı Yönetimi */}
                    <div className="col-lg-4">
                        <RecipientManager 
                            selectedRecipients={selectedRecipients}
                            onAddRecipients={handleAddRecipients}
                            onClearRecipients={handleClearRecipients}
                            professionals={professionals}
                            contactMessages={contactMessages}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateCampaign;